import { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { FolderInput, Folder, Pencil } from "lucide-react";

import { usePermissions } from "@/hooks/usePermissions";
import { useProjectFolderStore } from "@/hooks/useProjectFolderStore";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import type { AddFileToFolderConfirmation } from "@/stores/chat/repository";
import type { ProjectOption, FolderOption } from "@/stores/projects/repository";
import { cn } from "@/lib/utils";
import { ProjectStatusBadge } from "@/domains/projects/status/ProjectStatusBadge";
import { ProjectStatusFilter } from "@/domains/projects/status/ProjectStatusFilter";
import { useProjectStatusFilter } from "@/domains/projects/status/useProjectStatusFilter";

interface AddFileToFolderConfirmationViewProps {
  payload: AddFileToFolderConfirmation;
  /**
   * Called whenever the user changes the destination so the parent card can
   * forward `{ target_project_id, target_folder_id }` to the chatbot on
   * approval.
   */
  onOverridesChange: (overrides: Record<string, unknown>) => void;
  disabled?: boolean;
}

/**
 * Confirmation card body for the `add_file_to_folder` chatbot tool (WHA-3354).
 *
 * Shows the source filename + the agent's proposed destination, and lets the
 * user override the target project / folder before approving. The override
 * flows back through `onOverridesChange` → BuildingDataConfirmationCard
 * → submitToolConfirmation → /chatbot/query/answer.
 */
export const AddFileToFolderConfirmationView = observer(function AddFileToFolderConfirmationView({
  payload,
  onOverridesChange,
  disabled = false,
}: AddFileToFolderConfirmationViewProps) {
  const { canWriteFilesInProject } = usePermissions();
  const { currentOrganization, isReady: orgReady } = useOrganizationContext();
  const projectFolderStore = useProjectFolderStore();
  const orgId = currentOrganization?.id;
  const buildingId = payload.building_id;

  const [projectId, setProjectId] = useState<number>(payload.target.project_id);
  const [projectName, setProjectName] = useState<string>(payload.target.project_name);
  const [folderId, setFolderId] = useState<number | null>(payload.target.folder_id);
  const [folderPath, setFolderPath] = useState<string | null>(payload.target.folder_path);

  const [editingProject, setEditingProject] = useState(false);
  const [editingFolder, setEditingFolder] = useState(false);

  // Emit overrides upward when the chosen target diverges from the agent's
  // suggestion. We only post the fields the user actually changed so the
  // backend keeps the original values for anything left untouched.
  useEffect(() => {
    const overrides: Record<string, unknown> = {};
    if (projectId !== payload.target.project_id) {
      overrides.target_project_id = projectId;
    }
    if ((folderId ?? null) !== (payload.target.folder_id ?? null)) {
      overrides.target_folder_id = folderId;
    }
    onOverridesChange(overrides);
  }, [projectId, folderId, payload.target.project_id, payload.target.folder_id, onOverridesChange]);

  // Load project options on demand via the MST store.
  useEffect(() => {
    if (!editingProject) return;
    if (!orgReady || !orgId || !buildingId) return;
    if (projectFolderStore.hasProjectOptions(orgId, buildingId)) return;
    projectFolderStore.loadProjectOptions(orgId, buildingId).catch(() => {
      /* load failures surface via the store's own error state */
    });
  }, [editingProject, orgReady, orgId, buildingId, projectFolderStore]);

  // Load folder options on demand for the currently selected project.
  useEffect(() => {
    if (!editingFolder) return;
    if (!orgReady || !orgId || !buildingId || !projectId) return;
    if (projectFolderStore.hasFolderOptions(orgId, buildingId, projectId)) return;
    projectFolderStore.loadFolderOptions(orgId, buildingId, projectId).catch(() => {
      /* load failures surface via the store's own error state */
    });
  }, [editingFolder, orgReady, orgId, buildingId, projectId, projectFolderStore]);

  // Derived in render, not memoised: this component is an observer, so MobX
  // re-renders it whenever the store's options load or a project's status
  // changes, and the lists are a handful of rows. (A memo keyed on the store's
  // array length missed status changes and hid its real dependencies.)
  const projects: ProjectOption[] =
    orgId && buildingId
      ? projectFolderStore
          .getProjectOptions(orgId, buildingId)
          .map((p) => ({ id: p.id, name: p.name, status: p.status }))
          .filter((p) => canWriteFilesInProject(p.id))
      : [];

  const folders: FolderOption[] =
    orgId && buildingId && projectId
      ? projectFolderStore
          .getFolderOptions(orgId, buildingId, projectId)
          .map((f) => ({ id: f.id, folder_path: f.folder_path }))
      : [];

  const handlePickProject = useCallback((p: ProjectOption) => {
    setProjectId(p.id);
    setProjectName(p.name);
    // Project changed → folder context is stale; fall back to project root
    // until the user picks again.
    setFolderId(null);
    setFolderPath(null);
    setEditingProject(false);
  }, []);

  const handlePickFolder = useCallback((f: FolderOption | null) => {
    setFolderId(f ? f.id : null);
    setFolderPath(f ? f.folder_path : null);
    setEditingFolder(false);
  }, []);

  const folderLabel = folderPath || "Project root";

  return (
    <div className="flex flex-col gap-2 text-neutral-700">
      <div className="text-xs uppercase tracking-wide text-neutral-500">Save file</div>

      <div className="flex items-center gap-2">
        <FolderInput className="h-4 w-4 shrink-0 text-neutral-500" />
        <span className="truncate font-medium text-neutral-800">{payload.source.filename}</span>
      </div>

      <div className="rounded-md border border-neutral-200 bg-white p-2 text-[13px]">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-neutral-500">Project</div>
            <div className="truncate font-medium text-neutral-800">{projectName}</div>
          </div>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setEditingProject((s) => !s)}
            className={cn(
              "inline-flex shrink-0 items-center gap-1 rounded px-2 py-1 text-[11px]",
              "text-neutral-600 hover:bg-neutral-100 disabled:opacity-50"
            )}
          >
            <Pencil className="h-3 w-3" /> Change
          </button>
        </div>

        {editingProject && (
          <ProjectPicker projects={projects} currentId={projectId} onPick={handlePickProject} />
        )}

        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-neutral-500">Folder</div>
            <div className="flex items-center gap-1 truncate font-medium text-neutral-800">
              <Folder className="h-3.5 w-3.5 text-neutral-500" />
              <span className="truncate">{folderLabel}</span>
            </div>
          </div>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setEditingFolder((s) => !s)}
            className={cn(
              "inline-flex shrink-0 items-center gap-1 rounded px-2 py-1 text-[11px]",
              "text-neutral-600 hover:bg-neutral-100 disabled:opacity-50"
            )}
          >
            <Pencil className="h-3 w-3" /> Change
          </button>
        </div>

        {editingFolder && (
          <FolderPicker folders={folders} currentId={folderId} onPick={handlePickFolder} />
        )}
      </div>
    </div>
  );
});

function ProjectPicker({
  projects,
  currentId,
  onPick,
}: {
  projects: ProjectOption[];
  currentId: number;
  onPick: (p: ProjectOption) => void;
}) {
  const statusFilter = useProjectStatusFilter(projects, currentId);
  if (projects.length === 0) {
    return (
      <div className="mt-1 rounded border border-neutral-200 bg-neutral-50 p-2 text-[11px] text-neutral-500">
        No projects you can upload to in this building.
      </div>
    );
  }
  return (
    <>
      {statusFilter.showFilter && (
        <ProjectStatusFilter
          variant="inline"
          value={statusFilter.filter}
          onValueChange={statusFilter.setFilter}
          counts={statusFilter.counts}
          className="mt-1"
        />
      )}
      <ul className="mt-1 max-h-32 overflow-y-auto rounded border border-neutral-200 bg-neutral-50 text-[12px]">
        {statusFilter.visible.map((p) => (
          <li key={p.id}>
            <button
              type="button"
              onClick={() => onPick(p)}
              className={cn(
                "w-full px-2 py-1 text-left hover:bg-neutral-100",
                p.id === currentId && "bg-primary/10 font-medium text-primary"
              )}
            >
              <span className="inline-flex items-center gap-1.5">
                <ProjectStatusBadge variant="dot" status={p.status} />
                {p.name}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

function FolderPicker({
  folders,
  currentId,
  onPick,
}: {
  folders: FolderOption[];
  currentId: number | null;
  onPick: (f: FolderOption | null) => void;
}) {
  const sorted = useMemo(
    () => [...folders].sort((a, b) => (a.folder_path || "").localeCompare(b.folder_path || "")),
    [folders]
  );
  return (
    <ul className="mt-1 max-h-32 overflow-y-auto rounded border border-neutral-200 bg-neutral-50 text-[12px]">
      <li>
        <button
          type="button"
          onClick={() => onPick(null)}
          className={cn(
            "w-full px-2 py-1 text-left hover:bg-neutral-100",
            currentId === null && "bg-primary/10 font-medium text-primary"
          )}
        >
          Project root
        </button>
      </li>
      {sorted.map((f) => (
        <li key={f.id}>
          <button
            type="button"
            onClick={() => onPick(f)}
            className={cn(
              "w-full px-2 py-1 text-left hover:bg-neutral-100",
              f.id === currentId && "bg-primary/10 font-medium text-primary"
            )}
          >
            {f.folder_path}
          </button>
        </li>
      ))}
    </ul>
  );
}
